import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { buildWikiUrl } from '@/features/wiki/architecture';
import { queries, formatMoney } from '@/data';
import type { ComuneFull } from '@/data';
import { useDetailsStore } from './store';
import { MonthlyCharts } from './MonthlyCharts';
import { PhotoHero } from './PhotoHero';
import { AddToShortlistButton } from '@/features/shortlist';
import { scoreComune } from '@/lib/scoring/scoreComune';
import { PROFILE_PRESETS, DIMENSION_LABELS } from '@/lib/scoring/profilePresets';
import type { ProfilePresetId } from '@/lib/scoring/profilePresets';
import { useScoringStore } from '@/lib/scoring/store';
import { useCompareStore, MAX_COMPARE_ITEMS } from '@/features/compare/store';
import { useSession } from '@/lib/auth-client';
import { usePinsStore } from '@/features/pins';
import { useUnitTerms } from '@/features/map-core/useUnitTerms';
import { PinsNearbySection } from './PinsNearbySection';
import { useQuizStore } from '@/features/quiz';
import { emit } from '@/lib/events';
// Alias to avoid clashing with the existing scoreComune from @/lib/scoring,
// which scores against a manually-tuned ProfileWeights preset rather than
// the quiz-derived persona.
import { scoreComune as scoreComuneQuiz } from '@/features/recommender/comune-scoring';
import { PERSONAS } from '@/features/recommender/persona';
import { PropertyListingsSection } from '@/features/property/PropertyListingsSection';
import { AiHelperBubble } from '@/features/ai/AiHelperBubble';
import { useCountryRagSlices } from '@/features/ai/rag-context';
import { getPassport } from '@/features/quiz';
import { PropertyTaxDetail } from '@/features/property-tax';
import { uiLang } from '@/i18n/lang';

function fmt(value: number | null | undefined, digits = 1, suffix = ''): string {
  if (value == null || !isFinite(value)) return '—';
  return `${value.toFixed(digits)}${suffix}`;
}

function fmtInt(value: number | null | undefined, suffix = '', lang: 'en' | 'ru' = 'ru'): string {
  if (value == null || !isFinite(value)) return '—';
  return `${Math.round(value).toLocaleString(lang === 'ru' ? 'ru' : 'en')}${suffix}`;
}

// Localized unit suffixes. Kept inline (not via react-i18next interpolation)
// because they're used in formatter helpers + we want them as plain strings
// concatenated, not as JSX. EN units are SI/imperial-friendly; RU uses Russian.
const UNITS = {
  en: { km: ' km', m: ' m', mm: ' mm', h: ' h', mbps: ' Mbps', ms: ' ms', perKm2: ' ppl/km²' },
  ru: { km: ' км', m: ' м', mm: ' мм', h: ' ч', mbps: ' Мбит/с', ms: ' мс', perKm2: ' чел/км²' },
} as const;

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between border-b border-slate-50 py-1 text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800">{value}</span>
    </div>
  );
}

function Section({
  title,
  emoji,
  defaultOpen = true,
  children,
}: {
  title: string;
  emoji: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  return (
    <details
      open={defaultOpen}
      className="group border-b border-slate-100 last:border-b-0"
    >
      <summary className="-mx-2 flex cursor-pointer list-none items-center gap-1.5 rounded px-2 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900 focus-visible:bg-slate-50 focus-visible:outline-none">
        {emoji && (
          <span aria-hidden="true" className="shrink-0">
            {emoji}
          </span>
        )}
        <span className="flex-1 truncate">{title}</span>
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-4 w-4 shrink-0 text-slate-400 transition-transform duration-150 group-open:rotate-90"
        >
          <path
            fillRule="evenodd"
            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
            clipRule="evenodd"
          />
        </svg>
      </summary>
      <div className="pb-2">{children}</div>
    </details>
  );
}

function scoreColor(score: number): string {
  if (score >= 90) return '#16a34a';
  if (score >= 75) return '#65a30d';
  if (score >= 60) return '#ca8a04';
  if (score >= 45) return '#ea580c';
  return '#dc2626';
}

export function DetailsPanel() {
  const { t, i18n } = useTranslation();
  const lang: 'en' | 'ru' = uiLang(i18n.language);
  const u = UNITS[lang];
  const openIstat = useDetailsStore((s) => s.openIstat);
  const close = useDetailsStore((s) => s.close);

  const { data: meta } = useQuery(queries.meta());
  const { data: fullIndex } = useQuery({
    ...queries.fullIndex(meta?.version ?? ''),
    enabled: !!meta?.version && openIstat !== null,
  });

  const activePresetId = useScoringStore((s) => s.activePresetId);
  const setPreset = useScoringStore((s) => s.setPreset);
  const [showPresetPicker, setShowPresetPicker] = useState(false);
  const compareIstats = useCompareStore((s) => s.istats);
  const toggleCompare = useCompareStore((s) => s.toggle);
  const { data: session } = useSession();
  const openAddPinDialog = usePinsStore((s) => s.openAddDialog);
  // F5.4 compact mode — keep KPI row visible but hide section list while
  // the user pans the map. Persisted across opens so we don't fight the
  // user's preference every time.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('relocation_details_collapsed') === '1';
    } catch {
      return false;
    }
  });
  const toggleCollapsed = () => {
    setCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem('relocation_details_collapsed', next ? '1' : '0');
      } catch {
        /* storage blocked */
      }
      return next;
    });
  };

  const comune: ComuneFull | undefined = useMemo(() => {
    if (!openIstat || !fullIndex) return undefined;
    return fullIndex.byIstat.get(openIstat);
  }, [openIstat, fullIndex]);

  const activePreset = PROFILE_PRESETS.find((p) => p.id === activePresetId) ?? PROFILE_PRESETS[0];

  const scoreResult = useMemo(() => {
    if (!comune) return null;
    return scoreComune(comune, activePreset.weights);
  }, [comune, activePreset.weights]);

  // Escape closes
  useEffect(() => {
    if (openIstat === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openIstat, close]);

  if (openIstat === null) return null;

  return (
    <>
      {/* Mobile: backdrop для закрытия тапом вне (только <sm) */}
      <div
        onClick={close}
        className="fixed inset-0 z-[1009] bg-black/30 sm:hidden"
        aria-hidden="true"
      />
      <aside
        className={`absolute inset-x-0 bottom-0 z-[1010] flex flex-col overflow-hidden rounded-t-2xl bg-white/95 shadow-2xl backdrop-blur-sm pb-safe sm:inset-x-auto sm:bottom-40 sm:right-4 sm:z-[1000] sm:w-80 sm:rounded-lg ${
          // Collapsed: header + KPI row only (~160 px on desktop).
          // Photo is hidden, so max-h just caps accidental growth.
          // Desktop bottom raised to sm:bottom-40 (160px) so the panel sits
          // ABOVE the AI helper icon (bottom-24 + 48px = 144px + gap).
          // Expanded max-h uses calc(100%-15rem) — percentage of the map
          // container (the positioned parent), NOT 100vh. Subtracts 10rem
          // for the sm:bottom-40 offset + 5rem clearance for the PoiPanel
          // button / QuizProfileBanner above. Using vh here was the bug:
          // it ignored the global site header so the panel grew tall enough
          // to be clipped by <main> and disappear behind the header.
          collapsed ? 'max-h-[40vh] sm:max-h-[160px]' : 'max-h-[80dvh] sm:max-h-[calc(100%-15rem)]'
        }`}
      >
        {/* Mobile drag handle (visual only — swipe-to-close требует extra lib, пока тап в backdrop) */}
        <div className="flex justify-center py-1.5 sm:hidden" aria-hidden="true">
          <div className="h-1 w-10 rounded-full bg-slate-300" />
        </div>
        {/* Header */}
        <div className="flex items-start justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
          <div className="min-w-0 flex-1">
            <div className="truncate text-base font-bold text-slate-900">
              {comune?.name ?? '—'}
            </div>
            <div className="truncate text-[11px] text-slate-500">
              {comune ? `${comune.reg} · ${comune.prov} · ISTAT ${comune.istat}` : t('loading.default')}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {comune && <AddToShortlistButton istat={comune.istat} />}
            {comune && (
              <button
                type="button"
                onClick={() => toggleCompare(comune.istat)}
                disabled={!compareIstats.includes(comune.istat) && compareIstats.length >= MAX_COMPARE_ITEMS}
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors ${
                  compareIstats.includes(comune.istat)
                    ? 'border-blue-300 bg-blue-50 text-blue-900'
                    : compareIstats.length >= MAX_COMPARE_ITEMS
                    ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400'
                    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                }`}
                title={
                  compareIstats.includes(comune.istat)
                    ? t('compare.remove', { defaultValue: 'Remove from compare' })
                    : compareIstats.length >= MAX_COMPARE_ITEMS
                    ? t('compare.full', { defaultValue: 'Compare list is full (max 5)' })
                    : t('compare.add', { defaultValue: 'Add to compare' })
                }
              >
                ⊞ {compareIstats.includes(comune.istat) ? t('compare.inList', { defaultValue: 'Comparing' }) : t('compare.add', { defaultValue: 'Compare' })}
              </button>
            )}
            {comune && session && (
              <button
                type="button"
                onClick={() =>
                  openAddPinDialog({
                    lat: comune.lat,
                    lon: comune.lon,
                    comuneIstat: comune.istat,
                    comuneName: comune.name,
                  })
                }
                className="inline-flex items-center gap-1 rounded-md border border-purple-300 bg-purple-50 px-2 py-1 text-xs text-purple-900 hover:bg-purple-100"
                title={t('pins.add', { defaultValue: 'Pin this place' })}
                aria-label={t('pins.add', { defaultValue: 'Pin this place' })}
              >
                📌 {t('pins.shortAdd', { defaultValue: 'Pin' })}
              </button>
            )}
            <button
              type="button"
              onClick={toggleCollapsed}
              className="hidden h-10 w-10 items-center justify-center rounded text-slate-400 hover:bg-slate-200 hover:text-slate-700 sm:flex"
              title={
                collapsed
                  ? t('details.expand', { defaultValue: 'Expand panel' })
                  : t('details.collapse', { defaultValue: 'Collapse panel' })
              }
              aria-label={
                collapsed
                  ? t('details.expand', { defaultValue: 'Expand panel' })
                  : t('details.collapse', { defaultValue: 'Collapse panel' })
              }
              aria-expanded={!collapsed}
            >
              <span aria-hidden="true">{collapsed ? '▾' : '▴'}</span>
            </button>
            <button
              onClick={close}
              className="flex h-10 w-10 items-center justify-center rounded text-slate-400 hover:bg-slate-200 hover:text-slate-700"
              title={t('details.closeTooltip')}
              aria-label={t('details.close')}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-3">
        {!comune ? (
          <div className="py-4 text-center text-sm text-slate-400">{t('details.loading')}</div>
        ) : (
          <>
            {/* Wikipedia hero image (F5.5) — hidden when collapsed so the
                KPI row is always fully visible without scrolling */}
            {!collapsed && <PhotoHero comuneName={comune.name} />}
            {/* KPI row — always visible, even in collapsed mode */}
            <div className="grid grid-cols-4 gap-1 py-2">
              <KpiCell label={t('details.kpi.altitude', { defaultValue: 'Высота' })} value={`${comune.alt}${u.m}`} />
              <KpiCell label={t('details.kpi.tMean', { defaultValue: 'T° ср' })} value={`${comune.climate.t_mean.toFixed(1)}°`} />
              <KpiCell label={t('details.kpi.hospital', { defaultValue: 'Больница' })} value={`${comune.infra.hosp.toFixed(1)}км`} />
              <KpiCell label={t('details.kpi.internet', { defaultValue: 'Интернет' })} value={fmt(comune.internet?.dl, 0, 'М')} />
            </div>

            {/* When collapsed, hide everything below the KPI row */}
            {!collapsed && <>
            {/* Profile fit — live scoring engine (F4.0) */}
            {scoreResult && (
              <Section
                title={`${activePreset.emoji} ${t(activePreset.nameKey, { defaultValue: activePreset.id })}`}
                emoji=""
                defaultOpen
              >
                <div className="flex items-center justify-between py-1">
                  <span
                    className="rounded-full px-2 py-0.5 text-xs font-bold text-white"
                    style={{ background: scoreColor(scoreResult.score) }}
                  >
                    {scoreResult.score}/100
                  </span>
                  <button
                    onClick={() => setShowPresetPicker((o) => !o)}
                    className="text-[10px] text-slate-400 underline-offset-1 hover:text-slate-600 hover:underline"
                  >
                    {t('scoring.changeProfile', { defaultValue: 'Change profile' })}
                  </button>
                </div>

                {/* Preset picker */}
                {showPresetPicker && (
                  <div className="mb-2 flex flex-wrap gap-1">
                    {PROFILE_PRESETS.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => {
                          setPreset(p.id as ProfilePresetId);
                          setShowPresetPicker(false);
                        }}
                        className={`rounded-full px-2 py-0.5 text-[10px] transition-colors ${
                          p.id === activePresetId
                            ? 'bg-amber-200 text-amber-900 font-semibold'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {p.emoji} {t(p.nameKey, { defaultValue: p.id })}
                      </button>
                    ))}
                  </div>
                )}

                {/* Dimension bars — bar values are objective per-comune scores
                    (they don't depend on the profile); the profile only changes
                    how much each dimension matters. To make that visible we sort
                    by the active profile's weight (highest priority on top) and
                    tag each row with its ×weight, so switching profiles visibly
                    reorders and re-emphasises the list. */}
                <div className="space-y-1 pt-1">
                  {[...(Object.entries(scoreResult.components) as [string, number][])]
                    .sort(
                      (a, b) =>
                        (activePreset.weights[b[0] as keyof typeof DIMENSION_LABELS] ?? 0) -
                        (activePreset.weights[a[0] as keyof typeof DIMENSION_LABELS] ?? 0),
                    )
                    .map(([dim, val], idx) => {
                      const w = activePreset.weights[dim as keyof typeof DIMENSION_LABELS] ?? 0;
                      const pct = Math.round(val * 100);
                      const isTop = idx === 0 && w > 0;
                      return (
                        <div key={dim} className="flex items-center gap-2">
                          <span
                            className={`w-28 shrink-0 text-[10px] ${
                              isTop ? 'font-semibold text-slate-700' : 'text-slate-500'
                            }`}
                          >
                            {t(DIMENSION_LABELS[dim as keyof typeof DIMENSION_LABELS], { defaultValue: dim })}{' '}
                            <span
                              className="font-mono text-[8px] text-slate-300"
                              title={t('scoring.weightTooltip', { defaultValue: 'Weight in this profile' })}
                            >
                              ×{w % 1 === 0 ? w : w.toFixed(1)}
                            </span>
                          </span>
                          <div className="flex-1 rounded-full bg-slate-100 h-1.5">
                            <div
                              className="h-1.5 rounded-full transition-all"
                              style={{
                                width: `${pct}%`,
                                background: scoreColor(pct),
                              }}
                            />
                          </div>
                          <span className="w-7 text-right text-[10px] text-slate-400">{pct}</span>
                        </div>
                      );
                    })}
                </div>
              </Section>
            )}

            {/* Year-round trends — monthly bar charts */}
            <Section title={t('details.section.yearRound', { defaultValue: 'По месяцам' })} emoji="📊" defaultOpen={false}>
              <MonthlyCharts comune={comune} />
            </Section>

            {/* Climate */}
            <Section title={t('details.section.climate')} emoji="🌤" defaultOpen>
              <Row label={t('details.row.tMean', { defaultValue: 'Средняя T° (год)' })} value={fmt(comune.climate.t_mean, 1, '°C')} />
              <Row label={t('details.row.tJul', { defaultValue: 'T° июля' })} value={fmt(comune.climate.t_jul, 1, '°C')} />
              <Row label={t('details.row.tJan', { defaultValue: 'T° января' })} value={fmt(comune.climate.t_jan, 1, '°C')} />
              <Row label={t('details.row.hotDays', { defaultValue: 'Жарких дней' })} value={fmt(comune.climate.hot_days, 1)} />
              <Row label={t('details.row.humidity', { defaultValue: 'Влажность' })} value={fmt(comune.climate.hum, 0, '%')} />
              <Row label={t('details.row.precip', { defaultValue: 'Осадки' })} value={fmtInt(comune.climate.precip, u.mm, lang)} />
              <Row label={t('details.row.snowDays', { defaultValue: 'Снежных дней' })} value={fmt(comune.climate.snow, 0)} />
              <Row label={t('details.row.sunnyDays', { defaultValue: 'Солнечных дней' })} value={fmt(comune.climate.sunny, 0)} />
              <Row label={t('details.row.sunHours', { defaultValue: 'Сумма солн. часов' })} value={fmtInt(comune.climate.sun, u.h, lang)} />
            </Section>

            {/* Infra */}
            <Section title={t('details.section.infra')} emoji="🏥">
              <Row label={t('details.row.toHospital', { defaultValue: 'До больницы' })} value={fmt(comune.infra.hosp, 1, u.km)} />
              <Row label={t('details.row.toPharmacy', { defaultValue: 'До аптеки' })} value={fmt(comune.infra.pharm, 1, u.km)} />
              <Row label={t('details.row.toSchool', { defaultValue: 'До школы' })} value={fmt(comune.infra.school, 1, u.km)} />
              <Row label={t('details.row.toTrain', { defaultValue: 'До ЖД-станции' })} value={fmt(comune.infra.train, 1, u.km)} />
              <Row label={t('details.row.toAirport', { defaultValue: 'До аэропорта' })} value={fmt(comune.infra.air, 0, u.km)} />
              <Row label={t('details.row.toSupermarket', { defaultValue: 'До магазина' })} value={fmt(comune.infra.super, 1, u.km)} />
              {/* Горнолыжные строки — только там, где есть альпийский датасет.
                  «До рифуджо —» в Испании не просто пусто: рифуджо это
                  итальянское понятие, и строка обещает то, чего в стране нет. */}
              {comune.infra.hut != null && (
                <Row label={t('details.row.toHut', { defaultValue: 'До рифуджо' })} value={fmt(comune.infra.hut, 1, u.km)} />
              )}
              {comune.infra.ski != null && (
                <Row label={t('details.row.toSki', { defaultValue: 'До подъёмника' })} value={fmt(comune.infra.ski, 1, u.km)} />
              )}
              {comune.infra.ski_n != null && (
                <Row label={t('details.row.skiN20', { defaultValue: 'Подъёмников в 20км' })} value={comune.infra.ski_n} />
              )}
            </Section>

            {/* Internet — broadband telemetry exists only for countries whose
                dataset carries it; the whole section is omitted otherwise,
                the same way the tax section below hides itself. */}
            {comune.internet && (
              <Section title={t('details.section.internet')} emoji="📡">
                <Row label={t('details.row.dl', { defaultValue: 'Скорость download' })} value={fmt(comune.internet.dl, 1, u.mbps)} />
                <Row label={t('details.row.ul', { defaultValue: 'Скорость upload' })} value={fmt(comune.internet.ul, 1, u.mbps)} />
                <Row label={t('details.row.ping', { defaultValue: 'Пинг' })} value={fmt(comune.internet.ping, 0, u.ms)} />
              </Section>
            )}

            {/* Demo */}
            <Section title={t('details.section.demo')} emoji="👥">
              <Row label={t('details.row.population', { defaultValue: 'Население' })} value={fmtInt(comune.pop, '', lang)} />
              <Row label={t('details.row.density', { defaultValue: 'Плотность' })} value={fmt(comune.demo.density, 1, u.perKm2)} />
              {/* Relief zone is a national classification (ISTAT zona
                  altimetrica in Italy). Countries without one simply don't
                  get the row. */}
              {comune.zone && (
                <Row label={t('details.row.zone', { defaultValue: 'Зона рельефа' })} value={comune.zone.replace('_', ' ')} />
              )}
            </Section>

            {/* Tax (MEF addizionale comunale IRPEF) */}
            {comune.tax && comune.tax.irpef_pct != null && (
              <Section title={t('details.section.tax')} emoji="💶">
                <Row
                  label={t('details.row.irpefMax', { defaultValue: 'Доп. IRPEF (макс. ставка)' })}
                  value={fmt(comune.tax.irpef_pct, 2, '%')}
                />
                {comune.tax.irpef_esente != null && comune.tax.irpef_esente > 0 && (
                  <Row
                    label={t('details.row.irpefExempt', { defaultValue: 'Освобождение до дохода' })}
                    value={formatMoney(comune.tax.irpef_esente, meta, { perYear: true, lang })}
                  />
                )}
              </Section>
            )}

            {/* Property tax — country-level since per-comune IMU rates are
                Tier-3 (blocked on MEF dataset ingest). Still surfaces because
                anyone clicking into a comune to consider BUYING needs the
                second tax queue (IMU / purchase / capital gains). */}
            <Section
              title={t('details.section.propertyTax', { defaultValue: 'Property tax (IT)' })}
              emoji="🏠"
            >
              <PropertyTaxDetail countryCode="IT" compact />
              <p className="mt-2 font-mono text-[10px] text-ink-400">
                {t('details.propertyTax.perComuneNote', {
                  defaultValue: 'Per-comune IMU multiplier not yet in dataset — figures here are national bands.',
                })}
              </p>
            </Section>

            {/* Foreign residents (ISTAT RCS) */}
            {comune.foreign && (comune.foreign.total ?? 0) > 0 && (
              <Section title={t('details.section.foreign')} emoji="🌍">
                <Row label={t('details.row.foreignTotal', { defaultValue: 'Всего иностранцев' })} value={fmtInt(comune.foreign.total ?? null)} />
                <Row label={t('details.row.foreignRu', { defaultValue: '🇷🇺 Россия' })} value={`${fmtInt(comune.foreign.ru ?? null)} (${fmt(comune.foreign.ru_pct ?? null, 2, '%')})`} />
                <Row label={t('details.row.foreignUa', { defaultValue: '🇺🇦 Украина' })} value={`${fmtInt(comune.foreign.ua ?? null)} (${fmt(comune.foreign.ua_pct ?? null, 2, '%')})`} />
                <Row label={t('details.row.foreignBy', { defaultValue: '🇧🇾 Беларусь' })} value={`${fmtInt(comune.foreign.by ?? null)} (${fmt(comune.foreign.by_pct ?? null, 2, '%')})`} />
              </Section>
            )}

            {/* Econ — placeholder, скрыто пока econ нет в данных */}
            {comune.econ && (
              <Section title={t('details.section.econ')} emoji="💶">
                <Row
                  label={t('details.row.rent', { defaultValue: 'Аренда' })}
                  value={formatMoney(comune.econ.rent_eur_m2y ?? null, meta, { perM2: true, perYear: true, decimals: 1, lang })}
                />
                <Row
                  label={t('details.row.sale', { defaultValue: 'Продажа' })}
                  value={formatMoney(comune.econ.sale_eur_m2 ?? null, meta, { perM2: true, lang })}
                />
                <Row
                  label={t('details.row.income', { defaultValue: 'Средний доход' })}
                  value={formatMoney(comune.econ.income_eur ?? null, meta, { perYear: true, lang })}
                />
              </Section>
            )}

            {/* M2 — "Why this comune fits you" from quiz persona */}
            <PersonaFitSection comune={comune} />

            {/* M4 — live property listings for this comune (stub data today) */}
            <PropertyListingsSection istat={comune.istat} />

            {/* C8 — link back from comune to user's saved pins (silent when none) */}
            <PinsNearbySection istat={comune.istat} lat={comune.lat} lon={comune.lon} />

            {/* Italy guide — surface the encyclopedia chapters for the country
                a user is exploring at comune-level. The four chapters listed
                are the highest-intent for someone weighing a relocation move:
                visa, taxes, cost-of-living, property. Visible whenever a
                comune is open. Links use slug-based wiki URLs so the chapter
                pages prerender into the sitemap. */}
            <ItalyGuideLinks lang={lang} />
            </>}
          </>
        )}
      </div>
    </aside>
    {/* M5 — AI helper bubble. Context is the open comune so questions like
        "is this good for kids?" can be answered from the loaded metrics.
        `knowledge` adds the IT property-tax slice + passport-treatment so
        users can ask "what's the IMU here?" or "can I open a bank account
        with my Russian passport?" against verified data, not from memory. */}
    {comune && (
      <DetailsAiBubble comune={comune} lang={lang} />
    )}
    </>
  );
}

function DetailsAiBubble({ comune, lang }: { comune: ComuneFull; lang: 'en' | 'ru' }) {
  const quizAnswers = useQuizStore((s) => s.answers);
  const passportOption = getPassport(quizAnswers);
  const ragSlices = useCountryRagSlices(['IT'], passportOption, lang);
  return (
    <AiHelperBubble
      surface="details"
      placement="br-map"
      context={{
        comune: {
          name: comune.name,
          prov: comune.prov,
          reg: comune.reg,
          alt: comune.alt,
          pop: comune.pop,
          climate: comune.climate,
          infra: comune.infra,
          internet: comune.internet,
          demo: comune.demo,
          tax: comune.tax,
        },
        knowledge: ragSlices,
      }}
    />
  );
}

function KpiCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded bg-slate-50 px-1 py-1 text-center">
      <div className="text-[9px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-xs font-semibold text-slate-800">{value}</div>
    </div>
  );
}

// M2 — "Why this comune fits you" derived from the user's quiz persona +
// the comune's own metrics. Hides itself when the user hasn't taken the
// quiz (no answers in the store) or when no reason crosses the threshold.
function PersonaFitSection({ comune }: { comune: ComuneFull }) {
  const { t } = useTranslation();
  const terms = useUnitTerms();
  const answers = useQuizStore((s) => s.answers);
  const score = useMemo(() => {
    if (Object.keys(answers).length === 0) return null;
    return scoreComuneQuiz(comune, answers, t);
  }, [comune, answers, t]);

  // Fire `scoring_viewed` once per comune visit when the persona-fit
  // section actually renders (i.e. there are reasons to show). The
  // istat-keyed effect resets when the user opens a different comune.
  useEffect(() => {
    if (!score || score.reasons.length === 0) return;
    emit('scoring:viewed', {
      istat: comune.istat,
      score: Math.round(score.score * 100),
    });
  }, [comune.istat, score]);

  if (!score || score.reasons.length === 0) return null;

  const personaLabel = score.persona
    ? t(PERSONAS[score.persona].labelKey, { defaultValue: score.persona })
    : null;

  return (
    <details
      open
      className="group border-b border-slate-100 last:border-b-0"
    >
      <summary className="-mx-2 flex cursor-pointer list-none items-center gap-1.5 rounded px-2 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900">
        <span aria-hidden="true" className="shrink-0">🧭</span>
        <span className="flex-1 truncate">
          {t('quiz.result.personaWhyFits', { ...terms, defaultValue: 'Why this {{unit}} fits you' })}
          {personaLabel && (
            <span className="ml-1.5 rounded-full bg-terra-50 px-2 py-0.5 text-[10px] font-medium text-terra-700">
              {personaLabel}
            </span>
          )}
        </span>
        <span className="shrink-0 font-mono text-xs font-semibold tabular-nums text-terra-600">
          {Math.round(score.score * 100)}%
        </span>
      </summary>
      <ul className="space-y-1 pb-2">
        {score.reasons.map((reason, i) => (
          <li key={i} className="flex items-start gap-2 py-1 text-xs text-slate-700">
            <span
              aria-hidden="true"
              className="mt-[5px] inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-terra-600"
            />
            <span>{reason}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * Italy guide-chapter shortcuts surfaced inside the comune DetailsPanel.
 * Renders the four highest-intent wiki chapter links (visa, taxes, cost,
 * property) plus the hub link, so a user exploring a comune can leave
 * the map for the relevant country chapter in one tap.
 */
function ItalyGuideLinks({ lang }: { lang: 'en' | 'ru' }) {
  const { t } = useTranslation();
  const langKey = lang === 'ru' ? 'ru' : 'en';
  const items: Array<{ topic: 'visa' | 'taxes' | 'cost-of-living' | 'property'; emoji: string; labelKey: string; fallback: string }> = [
    { topic: 'visa',           emoji: '🛂', labelKey: 'details.italyGuide.visa',     fallback: 'Visa pathways' },
    { topic: 'taxes',          emoji: '🧮', labelKey: 'details.italyGuide.taxes',    fallback: 'Tax regimes' },
    { topic: 'cost-of-living', emoji: '💶', labelKey: 'details.italyGuide.cost',     fallback: 'Cost of living' },
    { topic: 'property',       emoji: '🏠', labelKey: 'details.italyGuide.property', fallback: 'Buying property' },
  ];
  return (
    <details open className="group border-b border-slate-100 last:border-b-0">
      <summary className="-mx-2 flex cursor-pointer list-none items-center gap-1.5 rounded px-2 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900">
        <span aria-hidden="true" className="shrink-0">📖</span>
        <span className="flex-1 truncate">
          {t('details.italyGuide.heading', { defaultValue: 'Read about Italy' })}
        </span>
      </summary>
      <ul className="space-y-0.5 pb-2">
        {items.map(({ topic, emoji, labelKey, fallback }) => (
          <li key={topic}>
            <Link
              to={buildWikiUrl({ lang: langKey, cc: 'IT', topic })}
              className="flex items-center gap-2 rounded px-2 py-1.5 text-xs text-slate-700 hover:bg-slate-50 hover:text-terra-700"
              data-testid={`details-italy-guide-${topic}`}
            >
              <span aria-hidden="true" className="text-sm leading-none">{emoji}</span>
              <span className="flex-1">{t(labelKey, { defaultValue: fallback })}</span>
              <span aria-hidden="true" className="text-ink-500">→</span>
            </Link>
          </li>
        ))}
        <li className="pt-1">
          <Link
            to={buildWikiUrl({ lang: langKey, cc: 'IT' })}
            className="block rounded px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-500 hover:bg-slate-50 hover:text-ink-900"
          >
            {t('details.italyGuide.hub', { defaultValue: 'Full Italy guide →' })}
          </Link>
        </li>
      </ul>
    </details>
  );
}
